"use client";

import { Vacancy, VacancyPlatformInfo, VacancySalaryInfo } from "@entities/vacancy";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ButtonLink, TVacancyCarouselSection } from "@shared/cms";
import { formatDate, getSlug, imageFallback } from "@shared/utils";
import { Button, Carousel, CarouselApi, CarouselContent, CarouselItem } from "@ubud-ui/react-ui-kit";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

export function VacancyCarouselSectionContent({ meta, data }: { data: Vacancy[]; meta: TVacancyCarouselSection }) {
  const [api, setApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrentSlide(api.selectedScrollSnap() + 1);
    };

    onSelect();

    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const handlePrevSlide = () => {
    if (!api) return;

    api.scrollPrev();
  };

  const handleNextSlide = () => {
    if (!api) return;

    api.scrollNext();
  };

  if (!data || (data && data.length <= 0)) {
    return <></>;
  }

  return (
    <section className="py-14 sm:py-24">
      <div className="container">
        <div className="text-2xl sm:text-4xl font-bold text-gray-700 text-center">{meta.title}</div>
        <div className="mt-8 mb-9 sm:mt-10 sm:mb-10 relative">
          <Carousel className="w-full" opts={{ align: "start" }} setApi={setApi}>
            <CarouselContent className="-ml-4">
              {data.map((item, index) => {
                return (
                  <CarouselItem key={item.title + "-" + index} className="pl-4 basis-[85%] md:basis-[45%] xl:basis-[40%]">
                    <Link href={`/lowongan-dalam-negeri/lowongan/${getSlug(item.id, item.title)}`}>
                      <div className="w-full cursor-pointer h-full bg-gray-50 rounded-xl border border-gray-200 relative overflow-hidden">
                        <div className="flex flex-col justify-between space-y-12 sm:space-y-[2.75rem] relative overflow-hidden p-6 sm:p-10">
                          <div>
                            <div className="absolute bottom-0 right-0 w-[7.3rem] h-[8.3rem] xl:w-32 xl:h-[10rem] rounded-bl-3xl sm:rounded-bl-none rounded-tl-3xl overflow-hidden bg-gray-100">
                              <Image
                                src={
                                  item.companyLogoUri ||
                                  item.job?.employer?.logoUri ||
                                  item.employer?.employerable?.logoUri ||
                                  "/blank-gray.webp"
                                }
                                alt="Company Logo"
                                width={64}
                                height={64}
                                className="w-full h-full object-cover bg-blank"
                                onError={imageFallback("/blank-gray.webp")}
                              />
                            </div>
                            <div className="space-y-2 mb-6">
                              <div className="text-xl sm:text-2xl font-bold text-gray-700 h-[3.9rem] overflow-hidden">
                                {item.title || item.job?.title || "-"}
                              </div>
                              <div className="text-sm sm:text-base font-medium text-gray-700 truncate">
                                {item.companyName || item.employer?.name || "-"}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 truncate">{item.cityName || item.region?.name || "..."}</div>
                            </div>
                            <div className="text-xs font-medium text-gray-500 bg-gray-200 rounded-full px-4 py-2 inline-block">
                              {item?.jobTypeName || item.jobType?.name || "..."}
                            </div>
                          </div>
                          <div>
                            <div className="space-y-1 sm:space-y-2 w-[60%]">
                              <div className="text-xs sm:text-sm text-gray-500">{meta.vacancyCard.salaryLabel}</div>
                              <div className="font-medium text-gray-700">
                                <VacancySalaryInfo
                                  meta={meta.vacancyCard.salaryInfo}
                                  currency={item.minSalaryCurrency}
                                  min={item.minSalaryAmount || 0}
                                  max={item.maxSalaryAmount || 0}
                                  show={item.showSalary}
                                />
                              </div>
                              {item.expiresAt && (
                                <div className="text-xs text-gray-700 mt-4">Lamar sebelum {formatDate(item.expiresAt, "dd MMM yyyy")}</div>
                              )}
                              <div className="text-xs text-primary border border-primary mt-3 rounded-full px-1.5 py-0.5 inline-flex">
                                <span>Tersisa</span>&nbsp;<strong>{item.availableQuota || 0} lowongan</strong>
                              </div>
                            </div>
                          </div>
                        </div>
                        <VacancyPlatformInfo
                          meta={meta.vacancyCard.platformInfo}
                          vacancy={item}
                          preset={true}
                          nativeClass="py-6 px-6 sm:px-10 border-t border-gray-200"
                          contentClass="text-sm"
                        />
                      </div>
                    </Link>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>
          <div className="absolute w-full top-1/2 z-10 flex items-center justify-center space-x-0">
            <Button
              className="absolute size-10 p-0 left-0 xl:-left-8 rounded-full -translate-x-1/3 md:-translate-x-full cursor-pointer"
              variant="outline"
              onClick={() => handlePrevSlide()}
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </Button>
            <Button
              className="absolute size-10 p-0 right-0 xl:-right-8 rounded-full translate-x-1/3 md:translate-x-full cursor-pointer"
              variant="outline"
              onClick={() => handleNextSlide()}
            >
              <ChevronRightIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>
        {meta.moreButton && (
          <div className="text-center">
            <ButtonLink {...meta.moreButton} />
          </div>
        )}
      </div>
    </section>
  );
}
